/* eslint-disable max-classes-per-file */

import {
  ControlGroup,
  FormGroup,
  HTMLSelect,
  InputGroup,
  Position,
  Switch,
  Tag,
  Tooltip,
  Classes,
  PopoverPosition
} from '@blueprintjs/core'
import { DateInput } from '@blueprintjs/datetime'
import React from 'react'
import Select from 'react-select'
import { formatDate as momentFormatDate, DATE_AT_TIME } from '../../../../../utils/dateHelpers'
import Client from '../../../../../client'
import { LoadableSelect } from '../../../../../components/Inputs'
import NullSafeNumericInput from '../../../../../components/NullSafeNumericInput'
import CHANNELS from '../../../../../constants/Channels'
import ListingTypes from '../../../../../constants/ebay/ListingTypes'
import { sortAlpha } from '../../../../../utils'
import { renderError, stylizeTitle } from '../Helpers'
import { Help } from '../../../../../components/Help'
import { UprightStyle } from '../../../../../types'
import { GutterTooltipError } from '../GutterTooltipError'
import { ProductFormGroup } from '../ProductFormGroup'
import { ConditionDescriptor, EbayOnChangeHandler } from '../types'

interface EbayInputProps {
  onChange: EbayOnChangeHandler
  value?: any
  error?: any
  errors?: any
  showRequiredLabel?: boolean
  errorTooltipOffset?: number
  errorTooltipPosition?: string
}

const auctionOptions = new Array(16).fill(null).map((_, i) => ({
  name: i === 0 ? 'Immediately' : `${i} day${i === 1 ? '' : 's'} from now`,
  value: i
}))

const rightElementCurrencyTag = currencyCode => {
  switch (currencyCode) {
    case 'USD':
      return null
    case 'CAD':
      return <Tag minimal>CAD</Tag>
    default:
      return null
  }
}

export const DispatchTimeMax: React.FC<EbayInputProps> = React.memo(
  ({ value, onChange, errors, showRequiredLabel, errorTooltipOffset, errorTooltipPosition }) => (
    <ProductFormGroup
      title="Handling Time (Days)"
      error={errors.ebayListingDispatchTimeMax}
      showRequiredLabel={showRequiredLabel}
    >
      <GutterTooltipError
        isOpen={errors.ebayListingDispatchTimeMax}
        offsetPx={errorTooltipOffset}
        position={errorTooltipPosition}
      >
        <NullSafeNumericInput
          value={value}
          minorStepSize={1}
          intent={errors.ebayListingDispatchTimeMax ? 'danger' : 'none'}
          onValueChange={(_, v) => onChange({ name: 'dispatchTimeMax', value: v })}
          className={Classes.FILL}
        />
      </GutterTooltipError>
    </ProductFormGroup>
  )
)
export const Quantity: React.FC<EbayInputProps & { placeholder?: string; disabled?: boolean; helperText?: string }> =
  React.memo(({ errors, ...props }) => (
    <FormGroup
      label={stylizeTitle('Quantity', errors.ebayListingQuantity)}
      helperText={renderError(errors.ebayListingQuantity) || (props.helperText ? props.helperText : '')}
    >
      <NullSafeNumericInput
        min={1}
        placeholder={props.placeholder}
        clampValueOnBlur
        disabled={props.disabled || false}
        minorStepSize={1}
        selectAllOnFocus
        onValueChange={(_, value) => props.onChange({ name: 'quantity', value })}
        value={props.value}
        intent={errors.ebayListingQuantity ? 'danger' : 'none'}
        fill
      />
    </FormGroup>
  ))
export const StartingPrice: React.FC<EbayInputProps & { placeholder?: string; currencyCode: string }> = React.memo(
  ({ error, showRequiredLabel, ...props }) => (
    <ProductFormGroup title="Starting Price" error={error} showRequiredLabel={showRequiredLabel}>
      <GutterTooltipError isOpen={error} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
        <NullSafeNumericInput
          min={1}
          placeholder={props.placeholder}
          clampValueOnBlur
          leftIcon="dollar"
          minorStepSize={0.01}
          selectAllOnFocus
          onValueChange={(_, value) => props.onChange({ name: 'startingPrice', value })}
          value={props.value}
          intent={error ? 'danger' : 'none'}
          fill
          rightElement={rightElementCurrencyTag(props.currencyCode)}
        />
      </GutterTooltipError>
    </ProductFormGroup>
  )
)

export const BestOfferAutoAcceptPrice: React.FC<
  EbayInputProps & { currencyCode: string; disabled?: boolean; disabledPlaceholder?: boolean; placeholder?: any }
> = React.memo(({ error, ...props }) => (
  <FormGroup
    label={stylizeTitle('Auto accept if greater than or equal to', error)}
    helperText={renderError(error)}
    disabled={props.disabled}
    className="best-offer-auto-accept-price-form-group"
  >
    <NullSafeNumericInput
      disabled={props.disabled}
      min={1}
      placeholder={props.disabled ? props.disabledPlaceholder || props.placeholder : props.placeholder}
      clampValueOnBlur
      leftIcon="dollar"
      minorStepSize={0.01}
      selectAllOnFocus
      onValueChange={(_, value) => props.onChange({ name: 'bestOfferAutoAcceptPrice', value })}
      value={props.value}
      intent={error ? 'danger' : 'none'}
      fill
      rightElement={rightElementCurrencyTag(props.currencyCode)}
    />
  </FormGroup>
))

export const MinimumBestOfferPrice: React.FC<
  EbayInputProps & { currencyCode: string; disabled?: boolean; disabledPlaceholder?: boolean; placeholder?: any }
> = React.memo(({ error, ...props }) => (
  <FormGroup
    label={stylizeTitle('Auto decline if less than', error)}
    helperText={renderError(error)}
    disabled={props.disabled}
  >
    <NullSafeNumericInput
      disabled={props.disabled}
      min={1}
      placeholder={props.disabled ? props.disabledPlaceholder || props.placeholder : props.placeholder}
      clampValueOnBlur
      leftIcon="dollar"
      minorStepSize={0.01}
      selectAllOnFocus
      onValueChange={(_, value) => props.onChange({ name: 'minimumBestOfferPrice', value })}
      value={props.value}
      intent={error ? 'danger' : 'none'}
      fill
      rightElement={rightElementCurrencyTag(props.currencyCode)}
    />
  </FormGroup>
))

export const BidIncrement: React.FC<EbayInputProps & { currencyCode: string }> = React.memo(({ error, ...props }) => (
  <FormGroup label={stylizeTitle('Bid Increment', error)} helperText={renderError(error)}>
    <NullSafeNumericInput
      min={1}
      minorStepSize={0.01}
      clampValueOnBlur
      selectAllOnFocus
      onValueChange={(_, value) => props.onChange({ name: 'bidIncrement', value })}
      value={props.value}
      intent={error ? 'danger' : 'none'}
      leftIcon="dollar"
      fill
      rightElement={rightElementCurrencyTag(props.currencyCode)}
    />
  </FormGroup>
))
export const ReservePrice: React.FC<EbayInputProps & { currencyCode: string }> = React.memo(({ error, ...props }) => (
  <FormGroup label={stylizeTitle('Reserve Price', error)} helperText={renderError(error)}>
    <NullSafeNumericInput
      min={0}
      clampValueOnBlur
      selectAllOnFocus
      minorStepSize={0.01}
      onValueChange={(_, s) => props.onChange({ name: 'reservePrice', value: s })}
      value={props.value}
      intent={error ? 'danger' : 'none'}
      leftIcon="dollar"
      fill
      rightElement={rightElementCurrencyTag(props.currencyCode)}
    />
  </FormGroup>
))

export const BuyItNowPrice: React.FC<EbayInputProps & { currencyCode: string; placeholder?: string }> = React.memo(
  ({ value, error, ...props }) => (
    <FormGroup label={stylizeTitle('Buy It Now Price', error)} helperText={renderError(error)}>
      <NullSafeNumericInput
        min={0}
        placeholder={props.placeholder}
        clampValueOnBlur
        selectAllOnFocus
        minorStepSize={0.01}
        onValueChange={(_, v) => props.onChange({ name: 'buyItNowPrice', value: v })}
        value={value}
        intent={error ? 'danger' : 'none'}
        leftIcon="dollar"
        fill
        rightElement={rightElementCurrencyTag(props.currencyCode)}
      />
    </FormGroup>
  )
)

export const HandlingPrice: React.FC<EbayInputProps & { currencyCode: string }> = React.memo(({ error, ...props }) => (
  <FormGroup label={stylizeTitle('Handling Price', error)} helperText={renderError(error)}>
    <NullSafeNumericInput
      min={0}
      minorStepSize={0.01}
      clampValueOnBlur
      selectAllOnFocus
      onValueChange={(_, value) => props.onChange({ name: 'handlingPrice', value })}
      value={props.value}
      leftIcon="dollar"
      intent={error ? 'danger' : 'none'}
      fill
      rightElement={rightElementCurrencyTag(props.currencyCode)}
    />
  </FormGroup>
))

export const AuctionStart: React.FC<EbayInputProps> = React.memo(({ error, ...props }) => (
  <FormGroup label={stylizeTitle('Auction Start', error)} helperText={renderError(error)}>
    <Select
      value={props.value}
      onChange={value => props.onChange({ name: 'auctionStart', value })}
      labelKey="name"
      valueKey="value"
      intent={error ? 'danger' : 'none'}
      clearable={false}
      simpleValue
      options={auctionOptions}
    />
  </FormGroup>
))

export const ProductListingDetail: React.FC<
  EbayInputProps & { label: string; name: string; detail?: any; className?: string }
> = React.memo(({ error, label, name, value, detail, onChange, className }) => {
  if (!detail) return null

  return (
    <FormGroup
      label={stylizeTitle(label, error)}
      helperText={renderError(error)}
      intent={error ? 'danger' : 'none'}
      className={className || ''}
    >
      <InputGroup
        onChange={e => onChange({ value: e.target.value, name })}
        value={value}
        placeholder="Does Not Apply"
      />
    </FormGroup>
  )
})

export const ListingDuration: React.FC<
  EbayInputProps & { includeBlank?: boolean; clearable?: boolean; placeholder?: string; disabled?: boolean }
> = React.memo(({ error, showRequiredLabel, ...props }) => {
  const options = [
    { name: '1 Day', value: 'Days_1' },
    { name: '3 Days', value: 'Days_3' },
    { name: '5 Days', value: 'Days_5' },
    { name: '7 Days', value: 'Days_7' },
    { name: '10 Days', value: 'Days_10' },
    { name: '30 Days', value: 'Days_30' },
    { name: "Good Til' Canceled", value: 'GTC' }
  ]
  if (props.includeBlank) {
    options.unshift({ name: 'None', value: '' })
  }
  return (
    <ProductFormGroup title="Listing Duration" error={error} showRequiredLabel={showRequiredLabel}>
      <GutterTooltipError isOpen={error} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
        <Select
          value={props.value}
          onChange={value => props.onChange({ name: 'listingDuration', value })}
          labelKey="name"
          valueKey="value"
          clearable={props.clearable || false}
          placeholder={props.placeholder || 'Select...'}
          simpleValue
          disabled={props.disabled}
          options={options}
        />
      </GutterTooltipError>
    </ProductFormGroup>
  )
})

export const ListingType: React.FC<
  EbayInputProps & { includeBlank?: boolean; clearable?: boolean; placeholder?: string; disabled?: boolean }
> = React.memo(({ error, onChange, showRequiredLabel, ...props }) => {
  const options = Object.entries(ListingTypes).reduce(
    (acc, [id, info]) => [...acc, { label: info.humanReadableName, value: id }],
    []
  )
  if (props.includeBlank) {
    options.unshift({ label: 'None', value: '' })
  }
  return (
    <ProductFormGroup title="Listing Type" error={error} showRequiredLabel={showRequiredLabel}>
      <GutterTooltipError isOpen={error} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
        <Select
          value={props.value}
          onChange={value => onChange({ name: 'listingType', value })}
          clearable={props.clearable || false}
          placeholder={props.placeholder || 'Select...'}
          disabled={props.disabled}
          simpleValue
          options={options}
        />
      </GutterTooltipError>
    </ProductFormGroup>
  )
})

export const RelatedProducts = ({ error, ...props }) => (
  <div className="ebay-product" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gridGap: '30px' }}>
    {(props.products || []).map(p => (
      <div
        onClick={() => props.onChange(p.epid)}
        role="button"
        tabIndex={0}
        key={p.epid}
        className="bp3-card bp3-elevation-1 bp3-interactive"
        style={{
          border: p.epid === props.value ? '1px solid #106BA3' : '1px solid transparent'
        }}
      >
        <div>
          <h6>{p.name}</h6>
          <img alt="" src={p.imageUrl} style={{ maxHeight: '130px', display: 'block', margin: 'auto' }} />
        </div>
      </div>
    ))}
  </div>
)

export const BestOfferEnabled: React.FC<EbayInputProps & { bestOfferEnabled: boolean; checked: boolean }> = React.memo(
  ({ bestOfferEnabled, checked = false, onChange }) => (
    <Switch
      disabled={!bestOfferEnabled}
      checked={checked}
      label={bestOfferEnabled ? 'Allow Best Offer' : 'Best Offer Unavailable for this Category'}
      onChange={() => onChange({ name: 'bestOfferEnabled', value: !checked })}
      inline
    />
  )
)

export const ConditionPicker: React.FC<EbayInputProps & { options: any }> = React.memo(
  ({ error, onChange, ...props }) => (
    <ProductFormGroup title="Condition" error={error} showRequiredLabel>
      <GutterTooltipError isOpen={error} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
        <Select
          value={props.value}
          onChange={value => {
            // conditionDescription must be reset if the condition is any kind of new (1000-1499)
            if (value >= 1000 && value <= 1499) onChange({ name: 'conditionDescription', value: '' })

            // condition descriptors (like "Ungraded Condition") are only applicable to a certain condition.
            // Otherwise Ebay will complain if a user changes between conditions and leave behind some descriptors for the other condition.
            onChange({ name: 'conditionDescriptors', value: null })

            return onChange({ name: 'condition', value })
          }}
          clearable={false}
          simpleValue
          options={sortAlpha(
            props.options.map(c => ({ value: c.value, label: c.displayName })),
            'label'
          )}
        />
      </GutterTooltipError>
    </ProductFormGroup>
  )
)

interface ConditionDescriptionProps extends EbayInputProps {
  condition: number // ebay's numeric enum for item condition
}

export const ConditionDescriptionDisabledHelp: React.FC = React.memo(() => {
  return (
    <Help>
      <p>Condition description is ignored by eBay for new items</p>
    </Help>
  )
})

export const ConditionDescription: React.FC<ConditionDescriptionProps> = React.memo(
  ({ value, onChange, error, condition }) => {
    // eBay ignores the condition description field if the item condition is new
    // https://developer.ebay.com/devzone/xml/docs/reference/ebay/additem.html#Request.Item.ConditionDescription
    const conditionIsNew = condition >= 1000 && condition <= 1499

    return (
      <FormGroup
        label={stylizeTitle('Condition Description', error)}
        helperText={error ? renderError(error) : 'Additional description for used items'}
        intent={error ? 'danger' : 'none'}
        disabled={conditionIsNew}
        labelInfo={conditionIsNew && <ConditionDescriptionDisabledHelp />}
      >
        <InputGroup
          value={value || ''}
          onChange={e => {
            return onChange({ name: 'conditionDescription', value: e?.currentTarget?.value })
          }}
          disabled={conditionIsNew}
        />
      </FormGroup>
    )
  }
)

interface ConditionDescriptorPickerProps extends EbayInputProps {
  conditionDescriptor: ConditionDescriptor

  // Example value:
  // {
  //   '40001': { value: '400013' },
  //   '27503': { additionalInfo: 'cert # 2019561246' }
  // }
  formConditionDescriptors?: {}
}

// For choosing things like "Card Condition", "Professional Grader", etc. for trading card categories.
export const ConditionDescriptorPicker: React.FC<ConditionDescriptorPickerProps> = React.memo(
  ({ error, onChange, ...props }) => {
    let input
    if (props.conditionDescriptor.conditionDescriptorConstraint.mode === 'FREE_TEXT') {
      // Currently only for the field Graded > Certification Number
      input = (
        <InputGroup
          value={
            (props.formConditionDescriptors &&
              props.formConditionDescriptors[props.conditionDescriptor.conditionDescriptorId]?.additionalInfo) ||
            ''
          }
          onChange={e => {
            onChange({
              name: 'conditionDescriptors',
              value: {
                ...props.formConditionDescriptors,
                [props.conditionDescriptor.conditionDescriptorId]: { additionalInfo: e?.currentTarget?.value }
              }
            })
          }}
        />
      )
    } else if (props.conditionDescriptor.conditionDescriptorConstraint.mode === 'SELECTION_ONLY') {
      // Many fields use this. Graded > Grade, Ungraded > Card Condition, etc.
      const options = props.conditionDescriptor.conditionDescriptorValues?.map(x => ({
        value: x.conditionDescriptorValueId,
        label: x.conditionDescriptorValueHelpText
          ? `${x.conditionDescriptorValueName} - ${x.conditionDescriptorValueHelpText}`
          : x.conditionDescriptorValueName
      }))

      input = (
        <Select
          value={
            props.formConditionDescriptors &&
            props.formConditionDescriptors[props.conditionDescriptor.conditionDescriptorId]?.value
          }
          onChange={value => {
            onChange({
              name: 'conditionDescriptors',
              value: {
                ...props.formConditionDescriptors,
                [props.conditionDescriptor.conditionDescriptorId]: { value }
              }
            })
          }}
          clearable={false}
          simpleValue
          options={options}
          // Sometimes "Certification Number" will appear on the same line as these Select components. Make these the same height visually.
          className="react-select-same-height-as-blueprint"
        />
      )
    }

    const hasAdditionalHelpText = props.conditionDescriptor.conditionDescriptorValues?.find(
      x => x.conditionDescriptorValueAdditionalHelpText && x.conditionDescriptorValueAdditionalHelpText.length > 0
    )

    let help

    if (hasAdditionalHelpText) {
      help = (
        <Help popoverPosition={PopoverPosition.RIGHT}>
          {props.conditionDescriptor.conditionDescriptorValues?.map(x => {
            return (
              <div key={x.conditionDescriptorValueId}>
                <p>
                  {x.conditionDescriptorValueHelpText
                    ? `${x.conditionDescriptorValueName} - ${x.conditionDescriptorValueHelpText}`
                    : x.conditionDescriptorValueName}
                </p>
                <ul style={{ marginTop: 0 }}>
                  {x.conditionDescriptorValueAdditionalHelpText?.map(helpText => (
                    <li key={helpText}>{helpText}</li>
                  ))}
                </ul>
              </div>
            )
          })}
        </Help>
      )
    }

    const required = props.conditionDescriptor.conditionDescriptorConstraint.usage === 'REQUIRED'

    return (
      <FormGroup
        label={props.conditionDescriptor.conditionDescriptorName}
        labelInfo={
          <div>
            {help} <span>{required ? 'Required' : ''}</span>
          </div>
        }
        className="float-right-formgroup-label"
      >
        <GutterTooltipError isOpen={error} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
          <div>{input}</div>
        </GutterTooltipError>
      </FormGroup>
    )
  }
)

export const StoreCategoryPicker: React.FC<EbayInputProps & { marketAccountId: number }> = React.memo(
  ({ error, onChange, marketAccountId, ...props }) => (
    <FormGroup
      label={stylizeTitle('Store Category', error)}
      helperText={renderError(error)}
      intent={error ? 'danger' : 'none'}
    >
      <LoadableSelect
        load={() => Client.getEbayStores(marketAccountId)}
        process={d =>
          d.storeCategories.map(s => ({
            value: s.externalId,
            label: s.fullName
          }))
        }
        onChange={value => onChange({ name: 'storeCategoryId', value })}
        value={props.value}
        autoload={props.value}
      />
    </FormGroup>
  )
)

type SellerProfilesProps = EbayInputProps & {
  errors?: any
  style?: React.CSSProperties
  shippingValue?: any
  returnValue?: any
  paymentValue?: any
  // TODO(multi-ebay-account) should make this NOT optional
  marketAccountId?: number
  disabledReason?: string
  shippingProfileShowRequiredLabel?: boolean
  returnProfileShowRequiredLabel?: boolean
  paymentProfileShowRequiredLabel?: boolean
  sectionOpen?: boolean
  offset?: {
    errorTooltipShippingProfileOffset: number
    errorTooltipReturnsProfileOffset: number
    errorTooltipPaymentProfileOffset: number
  }
}

const fullWidthStyle = { style: { width: '100%' } }
export class SellerProfiles extends React.PureComponent<SellerProfilesProps, { options: any; loading: boolean }> {
  constructor(props) {
    super(props)
    this.state = {
      options: [],
      loading: false
    }
  }

  componentDidMount() {
    this.getData()
  }

  getData = async () => {
    this.setState({ loading: true })
    try {
      const res = await Client.getEbaySellerProfilesV5(this.props.marketAccountId)
      this.setState({
        options: res.data.profiles,
        loading: false
      })
    } catch (error) {
      this.setState({
        options: [],
        loading: false
      })
    }
  }

  getOptions = key => {
    return this.state.options
      .filter(p => p.hidden === false && p.profileType === key)
      .map(v => ({ value: v.externalId, label: v.name }))
  }

  componentDidUpdate(prevProps: Readonly<SellerProfilesProps>) {
    if (prevProps.marketAccountId !== this.props.marketAccountId) {
      this.getData()
    }
  }

  render() {
    const errors = this.props.errors || {}
    const { onChange, disabledReason, sectionOpen, offset } = this.props
    const { loading } = this.state

    return (
      <div style={{ display: 'grid', gridGap: '20px', gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div>
          <ProductFormGroup
            title="Shipping Profile"
            error={errors.ebayListingShippingProfileId}
            showRequiredLabel={this.props.shippingProfileShowRequiredLabel}
            style={this.props.style}
          >
            <GutterTooltipError
              isOpen={errors.ebayListingShippingProfileId}
              offsetPx={sectionOpen && offset?.errorTooltipShippingProfileOffset}
              position={Position.LEFT}
            >
              <Tooltip
                targetProps={fullWidthStyle}
                position={Position.TOP}
                content={disabledReason}
                disabled={!disabledReason}
              >
                <Select
                  {...this.props}
                  options={this.getOptions('SHIPPING')}
                  noResultsText={loading ? 'Loading...' : 'No results'}
                  placeholder={loading ? 'Loading...' : 'Select...'}
                  value={this.props.shippingValue}
                  simpleValue
                  onChange={value => onChange({ name: 'shippingProfileId', value })}
                  disabled={!!disabledReason || loading}
                />
              </Tooltip>
            </GutterTooltipError>
          </ProductFormGroup>
        </div>
        <div>
          <ProductFormGroup
            title="Returns Profile"
            error={errors.ebayListingReturnProfileId}
            showRequiredLabel={this.props.returnProfileShowRequiredLabel}
            style={this.props.style}
          >
            <GutterTooltipError
              isOpen={errors.ebayListingReturnProfileId}
              offsetPx={sectionOpen && offset?.errorTooltipReturnsProfileOffset}
              position={Position.LEFT}
            >
              <Tooltip
                targetProps={fullWidthStyle}
                position={Position.TOP}
                content={disabledReason}
                disabled={!disabledReason}
              >
                <Select
                  {...this.props}
                  placeholder={loading ? 'Loading...' : 'Select...'}
                  options={this.getOptions('RETURN_POLICY')}
                  noResultsText={loading ? 'Loading...' : 'No results'}
                  value={this.props.returnValue}
                  simpleValue
                  onChange={value => onChange({ name: 'returnProfileId', value })}
                  disabled={!!disabledReason || loading}
                />
              </Tooltip>
            </GutterTooltipError>
          </ProductFormGroup>
        </div>
        <div>
          <ProductFormGroup
            title="Payment Profile"
            error={errors.ebayListingPaymentProfileId}
            showRequiredLabel={this.props.paymentProfileShowRequiredLabel}
            style={this.props.style}
          >
            <GutterTooltipError
              isOpen={errors.ebayListingPaymentProfileId}
              offsetPx={sectionOpen && offset?.errorTooltipPaymentProfileOffset}
              position={Position.RIGHT}
            >
              <Tooltip
                targetProps={fullWidthStyle}
                position={Position.TOP}
                content={disabledReason}
                disabled={!disabledReason}
              >
                <Select
                  {...this.props}
                  placeholder={loading ? 'Loading...' : 'Select...'}
                  options={this.getOptions('PAYMENT')}
                  noResultsText={loading ? 'Loading...' : 'No results'}
                  value={this.props.paymentValue}
                  simpleValue
                  onChange={value => onChange({ name: 'paymentProfileId', value })}
                  disabled={!!disabledReason || loading}
                />
              </Tooltip>
            </GutterTooltipError>
          </ProductFormGroup>
        </div>
      </div>
    )
  }
}

const scheduleTimePickerStyles: UprightStyle = {
  select: { maxWidth: 115 }
}

export class ScheduleTimePicker extends React.PureComponent<EbayInputProps & { disabled: boolean }, { select: any }> {
  constructor(props) {
    super(props)
    this.state = {
      select: props.value ? 'Scheduled' : 'Immediately'
    }
  }

  render() {
    const { error } = this.props
    const minDate = new Date()
    const maxDate = new Date()
    maxDate.setDate(maxDate.getDate() + 20)
    return (
      <FormGroup label={stylizeTitle('Start Time', error)} intent={error ? 'danger' : 'none'}>
        <ControlGroup fill vertical={false}>
          <div style={scheduleTimePickerStyles.select} className="schedule-time-picker-select-container">
            <HTMLSelect
              onChange={e => {
                this.setState({ select: e.target.value })
                if (e.target.value === 'Immediately') {
                  this.props.onChange({ value: null, name: 'scheduleTime' })
                }
              }}
              disabled={this.props.disabled}
              options={['Immediately', 'Scheduled'].map(o => ({ label: o, value: o }))}
              value={this.state.select}
            />
          </div>
          <DateInput
            formatDate={date => momentFormatDate(date, DATE_AT_TIME)}
            parseDate={str => new Date(str)}
            value={this.props.value}
            onChange={date => this.props.onChange({ name: 'scheduleTime', value: date })}
            timePrecision="minute"
            disabled={this.props.disabled || this.state.select === 'Immediately'}
            minDate={this.props.disabled ? undefined : minDate}
            maxDate={this.props.disabled ? undefined : maxDate}
            placeholder={this.state.select === 'Immediately' ? 'List Immediately' : 'Schedule Listing'}
            closeOnSelection={false}
          />
        </ControlGroup>
      </FormGroup>
    )
  }
}

export const UseTaxTable: React.FC<EbayInputProps & { checked: boolean }> = React.memo(
  ({ checked = false, onChange }) => (
    <Switch
      checked={checked}
      label="Use eBay tax table"
      onChange={() => onChange({ name: 'useTaxTable', value: !checked })}
      inline
    />
  )
)

export const EbayAccountSelector = props => {
  return (
    <>
      <FormGroup
        label={stylizeTitle('eBay Account', props.error)}
        helperText={renderError(props.error)}
        intent={props.error ? 'danger' : 'none'}
        className="nuke-top-margin-loadable-select"
      >
        <GutterTooltipError isOpen={props.error} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
          <LoadableSelect
            disabled={props.disabled}
            value={props.value}
            load={() => Client.getAllEbaySettings({ matchesSupplierId: props.productSupplierId })}
            autoload
            placeholder="Select an account..."
            clearable={false}
            process={d =>
              d.ebaySettings.map(({ account }) => {
                let label = account.label || account.username

                const matchesSupplierId = !props.productSupplierId || account.matchesSupplierId
                if (!matchesSupplierId) label += ' (Does not match selected supplier)'
                return { label, value: account.id, disabled: !matchesSupplierId }
              })
            }
            onChange={id => {
              props.onChangeChannelMarketAccount(CHANNELS.EBAY.id, id)
            }}
          />
        </GutterTooltipError>
      </FormGroup>
    </>
  )
}

/* eslint-enable max-classes-per-file */
